/**
 * Chave única do cache persistido do TanStack Query no localStorage.
 *
 * FONTE DA VERDADE: `main.tsx` (o persister) e TODOS os fluxos de fim de sessão
 * importam daqui pra nunca divergir. Bug histórico: o logout limpava a chave
 * `chatplus-query-cache` enquanto o persister salvava em `chatplus-query-cache:v2`
 * → o cache do tenant anterior nunca era apagado e vazava no login seguinte
 * (canais/listas de OUTRA instância apareciam por alguns segundos até o refetch).
 *
 * Bump o sufixo quando shapes de cache mudarem (ex: query → infiniteQuery) —
 * sem isso, cache persistido tenta hidratar shapes antigos no boot e crasha.
 */
export const PERSIST_CACHE_KEY = 'chatplus-query-cache:v2';

/**
 * Marca do último ponto sincronizado com o servidor (vide
 * `features/atendimento/reconexao.ts`). Mora aqui, junto do cache persistido,
 * porque tem exatamente o MESMO ciclo de vida: é estado desta sessão, e vazar
 * pra próxima faria o painel achar que está em dia sobre um período que nunca
 * viu — no mesmo navegador, possivelmente de outro cliente.
 *
 * Deixar a limpeza dela junto da do cache é o que torna impossível esquecer:
 * são sete os caminhos de fim de sessão (logout, logout de todos, revogação
 * pelo admin, troca de departamento, expiração do refresh…), e todos já passam
 * por `clearPersistedQueryCache`.
 */
export const SYNC_MARK_KEY = 'chatplus:sincronizado-ate';

/**
 * Hora LOCAL (relógio desta máquina, ms) em que a marca acima foi gravada.
 *
 * A marca é hora do SERVIDOR (o `agora` da resposta do delta) e o carimbo da
 * cópia salva é hora desta máquina (`Date.now()` do persister). Comparar as
 * duas direto erra pelo desvio entre os relógios — e relógio torto de atendente
 * já foi medido em produção (vide carimbo-da-bolha.ts). Gravando junto a hora
 * local do mesmo instante, a comparação com a cópia fica no MESMO relógio.
 *
 * Formato `"<ms locais>|<marca ISO>"`: o par vai numa chave só porque aba com
 * código antigo (aberta antes do deploy) grava só a marca — e um par meio
 * velho recuaria a marca de menos. Só vale se a marca dentro dele for a atual.
 */
export const SYNC_MARK_LOCAL_KEY = 'chatplus:sincronizado-ate-local';

/** Cópia salva mais velha que isso não é restaurada: o painel começa limpo. */
export const COPIA_VELHA_DEMAIS_MS = 12 * 60 * 60 * 1000;

/**
 * Lê o carimbo (`timestamp`, ms locais) da cópia salva sem desserializar o
 * cache inteiro. O persister grava `{"buster":…,"timestamp":…,"clientState":…}`
 * nessa ordem, então o carimbo está sempre no começo do texto.
 */
function carimboDaCopia(bruto: string): number | null {
  const m = /"timestamp":(\d{10,})/.exec(bruto.slice(0, 300));
  return m?.[1] ? Number(m[1]) : null;
}

export type PreparoDaCopia = 'sem_copia' | 'descartada' | 'marca_recuada' | 'em_dia';

/**
 * Prepara a cópia salva ANTES de o painel restaurá-la (chamar no `main.tsx`,
 * antes do primeiro render e antes de o socket existir).
 *
 * O defeito que isto fecha ("conversa velha que nem o F5 resolve", srv02,
 * 27/out): a cópia grande para de ser gravada — o persister falha calado,
 * provavelmente com o localStorage cheio — enquanto a marca, pequena, segue
 * avançando a cada reconexão. No F5 o painel restaurava a cópia de horas atrás
 * e perguntava ao servidor só o que mudou desde a marca recente: o que mudou no
 * meio nunca era corrigido.
 *
 * - Cópia com mais de 12 h → apagada (cópia + marca). O painel começa limpo,
 *   como depois de sair e entrar.
 * - Cópia mais velha que a marca → a marca recua para o instante da cópia, e o
 *   delta da entrada passa a cobrir o buraco. Se o buraco passar da janela do
 *   delta, o servidor manda recarregar e tudo vira suspeito (comportamento de
 *   sempre).
 *
 * Nunca lança: localStorage indisponível só deixa tudo como está.
 */
export function prepararCopiaDoCacheNaEntrada(agoraMs: number = Date.now()): PreparoDaCopia {
  try {
    const bruto = localStorage.getItem(PERSIST_CACHE_KEY);
    if (!bruto) return 'sem_copia';
    const copia = carimboDaCopia(bruto);
    if (copia === null) return 'em_dia';

    if (agoraMs - copia > COPIA_VELHA_DEMAIS_MS) {
      clearPersistedQueryCache();
      return 'descartada';
    }

    const marca = localStorage.getItem(SYNC_MARK_KEY);
    const marcaMs = marca ? Date.parse(marca) : Number.NaN;
    if (Number.isNaN(marcaMs)) return 'em_dia';

    // Mesmo relógio quando dá: hora local da gravação da marca contra o
    // carimbo local da cópia. Sem o par (marca de versão anterior) ou com par de
    // outra marca (aba antiga regravou só a marca), cai na comparação direta
    // com a hora do servidor.
    const [localTxt, marcaDoPar] = (localStorage.getItem(SYNC_MARK_LOCAL_KEY) ?? '').split('|');
    const localMs = Number(localTxt);
    const parValido = marcaDoPar === marca && Number.isFinite(localMs) && localMs > 0;
    const atraso = parValido ? localMs - copia : marcaMs - copia;
    if (atraso <= 0) return 'em_dia';

    const nova = new Date(marcaMs - atraso).toISOString();
    localStorage.setItem(SYNC_MARK_KEY, nova);
    localStorage.setItem(SYNC_MARK_LOCAL_KEY, `${copia}|${nova}`);
    return 'marca_recuada';
  } catch {
    return 'em_dia';
  }
}

/**
 * Limpa o cache persistido do TanStack. Chamar em TODO fim de sessão (logout,
 * logout-de-todos, revogação remota, expiração 401) ANTES do reload — senão a
 * próxima sessão (mesmo outro tenant no mesmo browser) restaura dados do tenant
 * anterior por alguns segundos (vazamento cross-tenant na UI).
 */
export function clearPersistedQueryCache(): void {
  try {
    localStorage.removeItem(PERSIST_CACHE_KEY);
    // Chave legada (pré-:v2) — remove por garantia.
    localStorage.removeItem('chatplus-query-cache');
    // Marca de sincronia: mesmo motivo do cache, vide SYNC_MARK_KEY.
    localStorage.removeItem(SYNC_MARK_KEY);
    localStorage.removeItem(SYNC_MARK_LOCAL_KEY);
  } catch {
    // localStorage indisponível/cheio — não bloqueia o logout.
  }
}
